import {
  applyResolvedTheme,
  readStoredThemePreference,
  resolveTheme,
  watchSystemTheme,
  writeStoredThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';

/**
 * 主題偏好的極簡外部 store（供 `useSyncExternalStore` 使用）。
 *
 * 不用 zustand：主題與 project-store 的生命週期完全無關（不進歷史、不標
 * dirty、不隨開新檔重置），混進去只會讓那顆 store 的語義變糊。
 */

let preference: ThemePreference = readStoredThemePreference();
let resolved: ResolvedTheme = resolveTheme(preference);

const listeners = new Set<() => void>();
let stopWatching: (() => void) | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

/** 重新解析並套用主題；解析結果有變才通知訂閱者。 */
function syncResolved(): void {
  const next = resolveTheme(preference);
  applyResolvedTheme(next);
  if (next === resolved) return;
  resolved = next;
  emit();
}

/**
 * `system` 模式下掛上 `matchMedia` 監聽，其他模式解除。
 * 使用者在 macOS 系統設定切換外觀時，app 因此不必重開就會跟著變。
 */
function updateSystemWatch(): void {
  if (preference === 'system') {
    stopWatching ??= watchSystemTheme(() => syncResolved());
    return;
  }
  stopWatching?.();
  stopWatching = null;
}

/**
 * 在 app 啟動最早期呼叫一次：套用已儲存的偏好並依需要掛上系統監聽。
 *
 * `index.html` 的 inline script 已經先把 class 打上去避免白閃，這裡只是把
 * 同一份狀態接手到模組層（兩者結果一致，不會造成第二次視覺跳動）。
 */
export function initTheme(): void {
  preference = readStoredThemePreference();
  resolved = resolveTheme(preference);
  applyResolvedTheme(resolved);
  updateSystemWatch();
}

export function getThemePreference(): ThemePreference {
  return preference;
}

export function getResolvedTheme(): ResolvedTheme {
  return resolved;
}

export function setThemePreference(next: ThemePreference): void {
  const changed = next !== preference;
  preference = next;
  writeStoredThemePreference(next);
  updateSystemWatch();
  syncResolved();
  // 偏好本身變了但解析結果沒變（例如 system→light 而系統本來就是淺色）
  // 時 syncResolved() 不會發事件，這裡補一次，否則切換 UI 不會更新選取態。
  if (changed) emit();
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 僅供測試：把模組層狀態與監聽還原成乾淨狀態。 */
export function resetThemeStoreForTests(): void {
  stopWatching?.();
  stopWatching = null;
  listeners.clear();
  preference = readStoredThemePreference();
  resolved = resolveTheme(preference);
}
